'use client'

import Link from 'next/link'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

type ProjectItem = {
  id: number
  slug: string
  title: string
  imageUrl: string
}

type Props = {
  projects: ProjectItem[]
}

const VISIBLE_COUNT = 5
const GAP = 30

export function MoreProjectsCarousel({ projects }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [viewportWidth, setViewportWidth] = useState<number | null>(null)
  const [page, setPage] = useState(0)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const isHovered = useRef(false)

  // Drag state
  const dragState = useRef({
    isDragging: false,
    startX: 0,
    startScroll: 0,
    didDrag: false,
  })
  const [isGrabbing, setIsGrabbing] = useState(false)
  const blockClickRef = useRef(false)

  useEffect(() => {
    const updateViewportWidth = () => setViewportWidth(window.innerWidth)
    updateViewportWidth()
    window.addEventListener('resize', updateViewportWidth)
    return () => window.removeEventListener('resize', updateViewportWidth)
  }, [])

  const visibleCount =
    viewportWidth === null
      ? VISIBLE_COUNT
      : viewportWidth < 768
        ? 1
        : viewportWidth < 1024
          ? 2
          : viewportWidth < 1280
            ? 3
            : VISIBLE_COUNT
  const totalPages = Math.ceil(projects.length / visibleCount)

  const updateScrollState = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 0)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    updateScrollState()
    el.addEventListener('scroll', updateScrollState, { passive: true })
    const ro = new ResizeObserver(updateScrollState)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', updateScrollState)
      ro.disconnect()
    }
  }, [updateScrollState])

  // Reset paging when the project list changes (render-phase adjustment)…
  const [prevProjects, setPrevProjects] = useState(projects)
  if (prevProjects !== projects) {
    setPrevProjects(projects)
    setPage(0)
  }

  // …and snap the scroll container back to the start (DOM = external system).
  useEffect(() => {
    const el = containerRef.current
    if (el) el.scrollTo({ left: 0, behavior: 'instant' as ScrollBehavior })
  }, [projects])

  // Auto-scroll — pauses on hover
  useEffect(() => {
    if (totalPages <= 1) return
    const interval = setInterval(() => {
      if (isHovered.current) return
      setPage((prev) => {
        const next = prev >= totalPages - 1 ? 0 : prev + 1
        const el = containerRef.current
        if (el) {
          const pageWidth = el.clientWidth + GAP
          el.scrollTo({ left: next * pageWidth, behavior: 'smooth' })
        }
        return next
      })
    }, 4000)
    return () => clearInterval(interval)
  }, [totalPages])

  const goToPage = useCallback(
    (index: number) => {
      const el = containerRef.current
      if (!el) return
      const pageWidth = el.clientWidth + GAP
      el.scrollTo({ left: index * pageWidth, behavior: 'smooth' })
      setPage(index)
    },
    [setPage],
  )

  const scrollByDir = useCallback(
    (dir: -1 | 1) => {
      const next = Math.min(Math.max(page + dir, 0), totalPages - 1)
      goToPage(next)
    },
    [goToPage, page, totalPages],
  )

  const items = useMemo(() => {
    if (projects.length <= visibleCount) return projects
    return projects
  }, [projects, visibleCount])

  // Mouse drag handlers
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    const el = containerRef.current
    if (!el) return
    blockClickRef.current = false
    dragState.current = {
      isDragging: true,
      startX: e.clientX,
      startScroll: el.scrollLeft,
      didDrag: false,
    }
    setIsGrabbing(true)
  }, [])

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const ds = dragState.current
    if (!ds.isDragging) return
    const el = containerRef.current
    if (!el) return
    const dx = ds.startX - e.clientX
    if (Math.abs(dx) > 8) {
      ds.didDrag = true
      blockClickRef.current = true
    }
    el.scrollLeft = ds.startScroll + dx
  }, [])

  const onPointerUp = useCallback(() => {
    const ds = dragState.current
    ds.isDragging = false
    setIsGrabbing(false)
    if (ds.didDrag) {
      window.setTimeout(() => {
        blockClickRef.current = false
      }, 100)
    }
  }, [])

  const handleCardClick = useCallback((e: React.MouseEvent, _slug: string) => {
    if (blockClickRef.current) {
      e.preventDefault()
    }
  }, [])

  if (projects.length === 0) return null

  return (
    <section className="relative z-10 w-full bg-white py-10 md:py-14">
      <div className="container-fluid mx-auto px-[30px] md:px-7">
        <h2 className="mb-6 text-[32px] font-medium leading-[38px] tracking-tight text-[#222] md:text-[32px]">
          More Projects
        </h2>

        <div
          className="relative"
          onMouseEnter={() => {
            isHovered.current = true
          }}
          onMouseLeave={() => {
            isHovered.current = false
          }}
        >
          {/* Scroll container */}
          <div
            ref={containerRef}
            className={`flex w-full snap-x snap-mandatory overflow-x-auto scrollbar-hide ${
              isGrabbing ? 'cursor-grabbing' : 'cursor-grab'
            }`}
            style={{ gap: GAP }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {items.map((item) => (
              <Link
                key={item.id}
                href={`/project/${item.slug}`}
                className="group shrink-0 snap-start select-none"
                draggable={false}
                style={{
                  width: `calc((100% - ${GAP * (visibleCount - 1)}px) / ${visibleCount})`,
                  minWidth: `calc((100% - ${GAP * (visibleCount - 1)}px) / ${visibleCount})`,
                }}
                onClick={(e) => handleCardClick(e, item.slug)}
              >
                <div className="relative h-[365px] w-full overflow-hidden bg-[#e8e8e8]">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    alt={item.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 pointer-events-none"
                    src={item.imageUrl}
                    draggable={false}
                  />
                </div>
                <p className="mt-[20px] pb-[10px] text-center text-[14px] font-medium text-[#222] transition-colors group-hover:text-[#1595a7] md:text-[15px]">
                  {item.title}
                </p>
              </Link>
            ))}
          </div>

          {/* Arrow buttons */}
          {canScrollLeft && (
            <button
              type="button"
              aria-label="Previous projects"
              onClick={() => scrollByDir(-1)}
              className="absolute left-0 top-1/2 flex -translate-y-1/2 items-center justify-center p-2 text-white"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          {canScrollRight && (
            <button
              type="button"
              aria-label="Next projects"
              onClick={() => scrollByDir(1)}
              className="absolute right-0 top-1/2 flex -translate-y-1/2 items-center justify-center p-2 text-white"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>

        {/* Dots — fixed 5, cycles every 5 pages */}
        {totalPages > 1 && (
          <div className="mt-1 flex items-center justify-center gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to dot ${i + 1}`}
                onClick={() => goToPage(i)}
                className={`rounded-full transition-all ${
                  i === page % 5 ? 'h-2 w-2 bg-black' : 'h-1.5 w-1.5 bg-[#d4d4d4] hover:bg-[#aaa]'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
